import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const sections = [{
  number: '01',
  heading: 'The Voluntary Enrollment Phase',
  subtitle: 'Part I — Months 1–4',
  paragraphs: ['To avoid the friction of a top-down mandate, the state introduces the NCJI as a premium "Capability Credential." The first phase is designed to establish the index as a high-status utility before any systemic integration begins.'],
  subsections: [{
    title: 'The "Sovereign Beta" Program',
    desc: 'High-status influencers, academics, and tech leaders are invited to take the index first. Their results are displayed on the Glassmorphism Interface, establishing the NCJI score as a new form of "Cognitive Social Capital."'
  }, {
    title: 'The Incentive Structure',
    desc: 'Citizens who voluntarily enroll and achieve "Participant" status (NCJI 65+) receive immediate "Fast-Track" benefits, such as expedited passport renewals, lower interest rates on state-backed loans, and priority access to high-demand government services.'
  }]
}, {
  number: '02',
  heading: 'The Institutional Pipeline Integration',
  subtitle: 'Part II — Months 5–8',
  paragraphs: ['The state begins the internal migration. The Institutional Pipeline becomes the mandatory gateway for all new appointments in high-stakes sectors.'],
  subsections: [{
    title: 'Non-Elective Roles',
    desc: 'All incoming applicants for the Judiciary, the Treasury, and National Security must pass the NCJI Protocol with a minimum score of 85 (Architect Tier).'
  }, {
    title: 'The Retroactive Audit',
    desc: 'Current high-ranking officials are offered a "Grace Period" to undergo Cognitive Hardening Modules. Those who refuse or fail to meet the threshold are transitioned into "Advisory Roles" with no final sign-off authority.'
  }]
}, {
  number: '03',
  heading: 'The Active Franchise & The Roadmap',
  subtitle: 'Part III — Months 9–12',
  paragraphs: ['The SyNeural operating system is deployed as the primary portal for all civic interactions. The third phase marks the public launch of the full civic judgment platform.'],
  subsections: [{
    title: 'The 90-Day Pulse',
    desc: 'The first national "Cognitive Check-in" is conducted. This is marketed as a "National Defense Exercise" to identify susceptibility to foreign disinformation campaigns.'
  }, {
    title: 'Growth Roadmaps',
    desc: 'The state officially opens the National Recalibration Utility. Citizens who score below the participation threshold are given their first 90-Day Growth Roadmap, granting them access to logic training and emotional regulation labs.'
  }, {
    title: 'The Sovereign Override',
    desc: 'The Sovereign Tier Defense Protocol is simulated for the first time, ensuring that the top 1% of scorers are ready to monitor "Cognitive Drift" and defend the National Mind.'
  }]
}, {
  number: '04',
  heading: 'The Constitutional Realignment',
  subtitle: 'Part IV — Month 12',
  paragraphs: ['At the end of the 12th month, the state holds a national referendum to codify Cognarchism. The "First Amendment" to the Cognocratic Charter marks the formal constitutional transition.'],
  subsections: [{
    title: 'The Shift',
    desc: 'Voting on complex systemic policy (such as the National Budget or Military Engagement) is officially decoupled from simple age-based eligibility and tied to the NCJI Verified Active Franchise.'
  }, {
    title: 'The Protections',
    desc: 'The charter includes "The Anti-Totalitarian Guardrail," ensuring that while access to power is filtered by competence, the protections of the law remain universal for all citizens, regardless of their tier.'
  }]
}, {
  number: '05',
  heading: 'The New Social Contract',
  subtitle: 'The Ultimate Goal',
  paragraphs: ['By the end of the first year, the state has transitioned from a system that rewards popularity to a system that rewards judgment. The "malfunctioning unit" is no longer a political opponent, but a biological problem to be solved through Self-Overcoming.', 'The Cognocracy State is now live.']
}];
export function TransitionAct() {
  return <article data-id="element-741">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-742">
        <div className="max-w-3xl mx-auto px-6" data-id="element-743">
          <FadeIn delay={0.1} direction="down" data-id="element-744">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-745">
              Legislative Blueprint
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-746">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-8 text-balance" data-id="element-747">
              The Transition Act: Implementing the First Phase of Cognarchism
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-748">
            <p className="text-lg md:text-xl text-cognarch-gray leading-relaxed mb-12 max-w-2xl" data-id="element-749">
              This legislative blueprint outlines the 12-month transition from a
              traditional administrative state to the first functioning layer of
              a Cognocratic Republic. It focuses on the deployment of the NCJI
              as a voluntary, high-status utility before its eventual
              integration into the constitutional fabric.
            </p>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-750">
            <Link to="/ncji" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-751">
              Explore the NCJI
            </Link>
            <Link to="/constitution" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-752">
              Read the Constitution
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Timeline indicator */}
      <section className="py-12 bg-cognarch-offwhite border-b border-cognarch-border" data-id="element-753">
        <div className="max-w-3xl mx-auto px-6" data-id="element-754">
          <FadeIn data-id="element-755">
            <div className="flex items-center justify-between text-xs uppercase tracking-widest text-cognarch-gray font-medium" data-id="element-756">
              <span data-id="element-757">Month 1</span>
              <div className="flex-1 mx-4 h-px bg-cognarch-border relative" data-id="element-758">
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-cognarch-charcoal rounded-full" data-id="element-759" />
                <div className="absolute left-1/3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-cognarch-charcoal rounded-full" data-id="element-760" />
                <div className="absolute left-2/3 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-cognarch-charcoal rounded-full" data-id="element-761" />
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-cognarch-charcoal rounded-full" data-id="element-762" />
              </div>
              <span data-id="element-763">Month 12</span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Body Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 0 ? 'bg-cognarch-white' : 'bg-cognarch-offwhite'}`} data-id="element-764">
          <div className="max-w-3xl mx-auto px-6" data-id="element-765">
            <FadeIn data-id="element-766">
              <div className="mb-8" data-id="element-767">
                <div className="flex items-baseline gap-6 mb-3" data-id="element-768">
                  <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-769">
                    {section.number}
                  </span>
                  <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-770">
                    {section.heading}
                  </h2>
                </div>
                {section.subtitle && <p className="text-xs uppercase tracking-widest text-cognarch-gray font-medium ml-[calc(2.5rem+1.5rem)] md:ml-[calc(3rem+1.5rem)]" data-id="element-771">
                    {section.subtitle}
                  </p>}
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-772">
              <div className="space-y-6" data-id="element-773">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-774">
                    {paragraph}
                  </p>)}

                {section.subsections && <div className="space-y-8 py-4 border-t border-cognarch-border mt-8 pt-8" data-id="element-775">
                    {section.subsections.map((sub, sIndex) => <div key={sIndex} className="flex gap-4" data-id="element-776">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5 shrink-0" data-id="element-777">
                          ◆
                        </span>
                        <div data-id="element-778">
                          <h3 className="text-cognarch-charcoal font-serif text-lg md:text-xl mb-2" data-id="element-779">
                            {sub.title}
                          </h3>
                          <p className="text-cognarch-gray leading-[1.8] text-base" data-id="element-780">
                            {sub.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Closing Statement Block */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-781">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-782">
          <FadeIn data-id="element-783">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-784" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-785">
              The Transition Act defines the operational pathway from theory to
              governance — a 12-month blueprint for deploying judgment as the
              foundation of constitutional order.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-786" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-787">
              From administration to architecture. From popularity to judgment.
              The Cognocracy is live.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}